import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmationService } from 'primeng/api';
import { WorkflowService } from 'src/app/core-module/services/workflow.service';
import { WorkflowTemplate } from 'src/app/core-module/models/Events/workflow-template';
import { WorkflowActionTemplate } from 'src/app/core-module/models/Events/workflow-action-template';
import { CreateWorkflowActionRequest } from 'src/app/core-module/models/Events/create-workflow-action-request';
import { UpdateWorkflowActionRequest } from 'src/app/core-module/models/Events/update-workflow-action-request';
import { ComplianceActionService } from 'src/app/core-module/services/compliance-action.service';
import { ActionUserRequest } from 'src/app/core-module/models/Compliance-Actions/action-user-request';
import { UserService } from 'src/app/core-module/services/user.service';
import { BasicUser } from 'src/app/core-module/models/basic-user';
import { ToastrService } from 'src/app/core-module/services/toastr.service';
import { Toastr } from 'src/app/core-module/models/Utils/toastr';
import { SharedActionBarComponent } from '../../../shared-action-bar/shared-action-bar.component';
import { AuditService } from 'src/app/core-module/services/audit.service';
import { FlattenedQuestionHolder } from 'src/app/core-module/models/Audits/flattened-question-holder';
import { MultiSelectModule } from 'primeng/multiselect';
import { AuditLocation } from 'src/app/core-module/models/Locations/audit-location';
import { ReferenceService } from 'src/app/core-module/services/reference.service';
import { CustomerTermPipe } from 'src/app/shared-module/pipes/customer-term.pipe';
import { CustomerContextService } from 'src/app/core-module/services/customer-context.service';

@Component({
  selector: 'app-workflow-actions',
  standalone: true,
  templateUrl: './workflow-actions.component.html',
  styleUrls: ['./workflow-actions.component.scss'],
  imports: [CommonModule, FormsModule, RouterModule, SelectModule, ConfirmPopupModule, SharedActionBarComponent, MultiSelectModule, CustomerTermPipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ConfirmationService]
})
export class WorkflowActionsComponent {
  private readonly customerContext = inject(CustomerContextService);
  workflowId: number = -1;
  workflow: WorkflowTemplate | null = null;
  loading: boolean = false;
  loadingActions: boolean = false;
  loadingUsers: boolean = false;
  saving: boolean = false;
  errorMessage: string = '';
  actionsError: string = '';
  locationsDirty: boolean = false;
  actions: WorkflowActionTemplate[] = [];
  actionForm: CreateWorkflowActionRequest = new CreateWorkflowActionRequest();
  actionAssignee: BasicUser | null = null;
  actionFieldErrors: { title?: string; due?: string; owner?: string; audit?: string } = {};
  editFieldErrors: Record<number, { title?: string; due?: string; owner?: string; audit?: string }> = {};
  actionEdits: Record<number, WorkflowActionTemplate> = {};
  actionEditAssignees: Record<number, BasicUser | null> = {};
  pendingCreates: { request: CreateWorkflowActionRequest; tempId: number }[] = [];
  pendingUpdates: WorkflowActionTemplate[] = [];
  pendingDeletes: number[] = [];
  actionsDirty: boolean = false;
  users: BasicUser[] = [];
  audits: FlattenedQuestionHolder[] = [];
  auditOptions: any[] = [];
  locations: AuditLocation[] = [];
  selectedLocationIds: number[] = [-1];
  savedLocationIds: number[] = [];
  pendingLocationIds: number[] | null = null;
  locationsLoading: boolean = false;
  locationsError: string = '';
  savingLocations: boolean = false;
  showLocationEditor: boolean = false;
  restrictMode: 'all' | 'specific' = 'all';
  actionVariations = [
    { id: 1, label: 'Standard action' },
    { id: 2, label: this.customerContext.displayText('Requires audit') }
  ];
  private tempActionId: number = -1;
  private readonly auditPlaceholderId = -1;
  private readonly locationPlaceholderId = -1;

  constructor(
    private route: ActivatedRoute,
    private workflowService: WorkflowService,
    private complianceActionService: ComplianceActionService,
    private userHelperService: UserService,
    private toast: ToastrService,
    private confirmationService: ConfirmationService,
    private auditService: AuditService,
    private referenceService: ReferenceService
  ) {}

  private workflowError(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback;
  }

  ngOnInit(){
    this.workflowId = Number(this.route.snapshot.paramMap.get('id')) || -1;
    this.loadUsers();
    this.loadAudits();
    this.loadLocations();
    this.loadWorkflow();
  }

  get hasUnsavedChanges(): boolean{
    return this.actionsDirty || this.locationsDirty || !!(this.actionForm?.Title?.trim() || this.actionForm?.Description?.trim());
  }

  get selectedLocationCount(): number{
    return this.filteredSelectedLocations(this.currentLocationIds()).length;
  }

  private loadUsers(){
    this.loadingUsers = true;
    const req = new ActionUserRequest({ EventID: -1, LocationID: -1 } as any);
    this.complianceActionService.getActionUsers(req).subscribe({
      next: list => {
        this.loadingUsers = false;
        this.users = this.userHelperService.sortUsers(list || []);
        if (!this.users.some(u => u.UserID === -1)){
          const placeholder = new BasicUser({ UserID: -1, DisplayName: 'Please Select...', Seq: 0, UserTypeID: 0 }) as any;
          (placeholder as any).Disabled = true;
          this.users.unshift(placeholder);
        }
        if (!this.actionAssignee){
          this.actionAssignee = this.users.find(u => u.UserID === -1) || null;
        }
      },
      error: () => {
        this.loadingUsers = false;
        this.users = [];
      }
    });
  }

  private loadWorkflow(){
    this.loading = true;
    this.workflowService.getWorkflows().subscribe({
      next: list => {
        this.workflow = (list || []).find(w => w.WorkflowID === this.workflowId) || null;
        this.loading = false;
        if (!this.workflow){
          this.errorMessage = 'Workflow not found.';
          return;
        }
        this.actionForm = new CreateWorkflowActionRequest({ WorkflowID: this.workflowId, ActionVariationID: 1, AuditID: this.auditPlaceholderId });
        this.loadActions();
      },
      error: error => {
        this.loading = false;
        this.errorMessage = this.workflowError(error, 'Unable to load workflow right now.');
      }
    });
  }

  private loadActions(){
    if (!this.workflowId || this.workflowId === -1){
      return;
    }
    this.loadingActions = true;
    this.actionsError = '';
    this.workflowService.getWorkflowActions(this.workflowId).subscribe({
      next: list => {
        this.actions = list || [];
        this.loadingActions = false;
        if (!this.actionAssignee){
          this.actionAssignee = this.users.find(u => u.UserID === -1) || null;
        }
        (this.actions || []).forEach(a => {
          if (!this.actionEditAssignees[a.WorkflowActionID]){
            this.actionEditAssignees[a.WorkflowActionID] = this.users.find(u => u.UserID === a.DefaultOwnerUserID) || this.users.find(u => u.UserID === -1) || null;
          }
          if (!a.ActionVariationID){
            a.ActionVariationID = 1;
          }
        });
        this.ensureValidAuditSelections();
      },
      error: error => {
        this.loadingActions = false;
        this.actionsError = this.workflowError(error, 'Unable to load actions.');
      }
    });
  }

  addWorkflowAction(){
    const form = this.actionForm || new CreateWorkflowActionRequest({ WorkflowID: this.workflowId });
    const title = form.Title?.trim();
    const errors: { title?: string; due?: string; owner?: string; audit?: string } = {};
    if (!title){
      errors.title = 'Please enter an action title.';
    }
    if (form.DefaultDueInDays === null || form.DefaultDueInDays === undefined){
      errors.due = 'Please enter due in days.';
    } else if (form.DefaultDueInDays < 0){
      errors.due = 'Due in days cannot be negative.';
    }
    const assignee = this.actionAssignee;
    if (!assignee || assignee.UserID === -1){
      errors.owner = 'Please select an owner.';
    }
    if ((form.ActionVariationID ?? 1) === 2){
      if (!form.AuditID || form.AuditID <= 0 || form.AuditID === this.auditPlaceholderId){
        errors.audit = this.customerContext.displayText('Please select the required audit.');
      }
    } else {
      form.AuditID = null;
    }
    this.actionFieldErrors = errors;
    if (errors.title || errors.due || errors.owner || errors.audit){
      return;
    }
    form.DefaultOwnerUserID = assignee && assignee.UserID !== -1 ? assignee.UserID : null;
    form.DefaultOwnerTypeID = assignee && assignee.UserID !== -1 ? assignee.UserTypeID : null;
    const tempId = this.tempActionId--;
    const staged = new WorkflowActionTemplate({
      WorkflowActionID: tempId,
      WorkflowID: this.workflowId,
      Title: form.Title?.trim() || '',
      Description: form.Description || '',
      DefaultDueInDays: form.DefaultDueInDays ?? null as any,
      IsMandatory: form.IsMandatory,
      DefaultOwnerUserID: form.DefaultOwnerUserID ?? undefined,
      DefaultOwnerTypeID: form.DefaultOwnerTypeID ?? undefined,
      ActionVariationID: form.ActionVariationID ?? 1,
      AuditID: form.AuditID ?? null
    });
    this.pendingCreates.push({ request: { ...form }, tempId });
    this.actions = [staged, ...(this.actions || [])];
    this.ensureValidAuditSelections();
    this.actionForm = new CreateWorkflowActionRequest({ WorkflowID: this.workflowId, ActionVariationID: 1, AuditID: this.auditPlaceholderId });
    this.actionAssignee = this.users.find(u => u.UserID === -1) || null;
    this.actionFieldErrors = {};
    this.actionsDirty = true;
  }

  startEdit(action: WorkflowActionTemplate){
    this.actionEdits[action.WorkflowActionID] = { ...action };
    const current = this.actionEdits[action.WorkflowActionID];
    if (current?.ActionVariationID === 2 && (!current.AuditID || current.AuditID <= 0)){
      current.AuditID = this.auditPlaceholderId;
    }
    if (!this.actionEditAssignees[action.WorkflowActionID]){
      this.actionEditAssignees[action.WorkflowActionID] = this.users.find(u => u.UserID === action.DefaultOwnerUserID) || this.users.find(u => u.UserID === -1) || null;
    }
  }

  cancelEdit(actionId: number){
    delete this.actionEdits[actionId];
    delete this.actionEditAssignees[actionId];
    delete this.editFieldErrors[actionId];
  }

  confirmEdit(actionId: number){
    const edit = this.actionEdits[actionId];
    if (!edit){ return; }
    const errors: { title?: string; due?: string; owner?: string; audit?: string } = {};
    if (!edit.Title || !edit.Title.trim()){
      errors.title = 'Please enter an action title.';
    }
    if (edit.DefaultDueInDays === null || edit.DefaultDueInDays === undefined){
      errors.due = 'Please enter due in days.';
    } else if (edit.DefaultDueInDays < 0){
      errors.due = 'Due in days cannot be negative.';
    }
    const selected = this.actionEditAssignees[actionId];
    if (!selected || selected.UserID === -1){
      errors.owner = 'Please select an owner.';
    }
    this.editFieldErrors[actionId] = errors;
    if ((edit.ActionVariationID ?? 1) === 2){
      if (!edit.AuditID || edit.AuditID <= 0 || edit.AuditID === this.auditPlaceholderId){
        errors.audit = this.customerContext.displayText('Please select the required audit.');
      }
    } else {
      edit.AuditID = null;
    }
    if (errors.title || errors.due || errors.owner || errors.audit){
      return;
    }
    edit.DefaultOwnerUserID = selected?.UserID ?? null as any;
    edit.DefaultOwnerTypeID = selected?.UserTypeID ?? null as any;
    const list = this.actions || [];
    const idx = list.findIndex(a => a.WorkflowActionID === actionId);
    if (idx >= 0){
      list[idx] = { ...edit };
      this.actions = [...list];
    }
    if (actionId < 0){
      this.pendingCreates = this.pendingCreates.map(c => c.tempId === actionId ? { ...c, request: new CreateWorkflowActionRequest({
        WorkflowID: this.workflowId,
        Title: edit.Title,
        Description: edit.Description,
        DefaultDueInDays: edit.DefaultDueInDays,
        IsMandatory: edit.IsMandatory,
        DefaultOwnerUserID: edit.DefaultOwnerUserID,
        DefaultOwnerTypeID: edit.DefaultOwnerTypeID,
        ActionVariationID: edit.ActionVariationID ?? 1,
        AuditID: edit.AuditID ?? null
      }) } : c);
    } else {
      this.pendingUpdates = this.pendingUpdates.filter(p => p.WorkflowActionID !== actionId);
      this.pendingUpdates.push({ ...edit });
    }
    delete this.actionEdits[actionId];
    delete this.editFieldErrors[actionId];
    delete this.actionEditAssignees[actionId];
    this.actionsDirty = true;
    this.actionsError = '';
  }

  markDelete(actionId: number){
    this.actions = (this.actions || []).filter(a => a.WorkflowActionID !== actionId);
    if (actionId < 0){
      this.pendingCreates = this.pendingCreates.filter(c => c.tempId !== actionId);
    } else {
      this.pendingDeletes = [...this.pendingDeletes.filter(d => d !== actionId), actionId];
    }
    this.actionsDirty = true;
  }

  confirmDeleteAction(event: Event, actionId: number){
    const target = event.currentTarget as HTMLElement | null;
    if (!target){ return; }
    this.confirmationService.confirm({
      target,
      key: 'workflowActionConfirm',
      header: 'Delete action',
      message: 'Are you sure you want to delete this action?',
      defaultFocus: 'reject',
      accept: () => this.markDelete(actionId),
      reject: () => {}
    });
  }

  onConfirmAccept(confirmation: any){
    confirmation?.acceptEvent?.emit();
    this.confirmationService.close();
  }

  onConfirmReject(confirmation: any){
    confirmation?.rejectEvent?.emit();
    this.confirmationService.close();
  }

  actionsFor(): WorkflowActionTemplate[] {
    const list = this.actions || [];
    return [...list].sort((a,b) => {
      const aDue = a.DefaultDueInDays ?? Number.MAX_SAFE_INTEGER;
      const bDue = b.DefaultDueInDays ?? Number.MAX_SAFE_INTEGER;
      if (aDue !== bDue){
        return aDue - bDue;
      }
      return (a.Seq ?? 0) - (b.Seq ?? 0);
    });
  }

  saveAllChanges(){
    if (!this.workflowId || this.workflowId === -1){
      return;
    }
    const actionCreates = this.pendingCreates.map(c => this.workflowService.createWorkflowAction(this.workflowId, c.request));
    const actionUpdates = this.pendingUpdates.map(u => this.workflowService.updateWorkflowAction(new UpdateWorkflowActionRequest({
      WorkflowID: this.workflowId,
      WorkflowActionID: u.WorkflowActionID,
      Title: u.Title,
      Description: u.Description,
      DefaultDueInDays: u.DefaultDueInDays,
      IsMandatory: u.IsMandatory,
      DefaultOwnerUserID: u.DefaultOwnerUserID,
      DefaultOwnerTypeID: u.DefaultOwnerTypeID,
      ActionVariationID: u.ActionVariationID ?? 1,
      AuditID: u.AuditID ?? null
    })));
    const actionDeletes = this.pendingDeletes.map(id => this.workflowService.deleteWorkflowAction({ WorkflowID: this.workflowId, WorkflowActionID: id }));
    const locationSave = (this.locationsDirty && this.pendingLocationIds !== null)
      ? [this.workflowService.saveWorkflowLocations(this.workflowId, this.pendingLocationIds)]
      : [];
    const all = [...actionCreates, ...actionUpdates, ...actionDeletes, ...locationSave];
    if (all.length === 0){
      this.actionsDirty = false;
      this.locationsDirty = false;
      return;
    }
    this.saving = true;
    import('rxjs').then(rx => {
      rx.forkJoin(all as any).subscribe({
        next: () => {
          this.saving = false;
          this.actionsDirty = false;
          if (this.locationsDirty){
            this.savedLocationIds = this.pendingLocationIds || [];
            this.applySelectedLocations(this.savedLocationIds);
            this.restrictMode = this.savedLocationIds.length > 0 ? 'specific' : 'all';
            this.pendingLocationIds = null;
            this.locationsDirty = false;
          }
          this.pendingCreates = [];
          this.pendingUpdates = [];
          this.pendingDeletes = [];
          this.toast.addToastr(new Toastr({ Type: 2, Message: 'Changes saved.' }));
          this.loadActions();
        },
        error: error => {
          this.saving = false;
          this.toast.addToastr(new Toastr({ Type: 3, Message: this.workflowError(error, 'Unable to save changes.') }));
        }
      });
    });
  }

  onSaveRequested(){
    if (this.saving){
      return;
    }
    this.saveAllChanges();
  }

  onUndoRequested(){
    if (this.saving){
      return;
    }
    this.resetActionChanges();
    this.loadActions();
    this.resetLocationChanges();
  }

  private resetActionChanges(){
    this.actions = [];
    this.actionForm = new CreateWorkflowActionRequest({ WorkflowID: this.workflowId, ActionVariationID: 1, AuditID: this.auditPlaceholderId });
    this.actionAssignee = this.users.find(u => u.UserID === -1) || null;
    this.actionFieldErrors = {};
    this.actionEdits = {};
    this.actionEditAssignees = {};
    this.pendingCreates = [];
    this.pendingUpdates = [];
    this.pendingDeletes = [];
    this.actionsDirty = false;
  }

  private resetLocationChanges(){
    this.pendingLocationIds = null;
    this.locationsDirty = false;
    this.applySelectedLocations(this.savedLocationIds);
    this.restrictMode = this.savedLocationIds.length > 0 ? 'specific' : 'all';
  }

  actionWord(count?: number | null): string{
    const value = count ?? 0;
    return value === 1 ? 'action' : 'actions';
  }

  defaultOwnerName(action: WorkflowActionTemplate): string{
    if (!action?.DefaultOwnerUserID && action?.DefaultOwnerUserID !== 0){
      return '';
    }
    const user = this.users.find(u => u.UserID === action.DefaultOwnerUserID);
    return user?.DisplayName || '';
  }

  trackByAction(_index: number, action: WorkflowActionTemplate): number{
    return action?.WorkflowActionID ?? _index;
  }

  auditName(id?: number | null): string{
    if (!id){ return ''; }
    return this.audits.find(a => a.AuditID === id)?.AuditName || '';
  }

  onAddVariationChange(value: number){
    this.actionForm.ActionVariationID = value;
    if (value === 2){
      if (!this.actionForm.AuditID || this.actionForm.AuditID <= 0){
        this.actionForm.AuditID = this.auditPlaceholderId;
      }
    } else {
      this.actionForm.AuditID = null;
    }
  }

  onEditVariationChange(actionId: number, value: number){
    const edit = this.actionEdits[actionId];
    if (!edit){ return; }
    edit.ActionVariationID = value;
    if (value === 2){
      if (!edit.AuditID || edit.AuditID <= 0){
        edit.AuditID = this.auditPlaceholderId;
      }
    } else {
      edit.AuditID = null;
    }
  }

  get isLocationRestricted(): boolean{
    return this.filteredSelectedLocations(this.currentLocationIds()).length > 0;
  }

  openLocations(){
    if (this.showLocationEditor){
      this.closeLocations(false);
      return;
    }
    this.showLocationEditor = true;
    const currentIds = this.currentLocationIds();
    this.restrictMode = currentIds.length > 0 ? 'specific' : 'all';
    this.applySelectedLocations(currentIds);
    this.locationsError = '';
  }

  closeLocations(revert: boolean = true){
    this.showLocationEditor = false;
    this.locationsError = '';
    if (revert){
      this.locationsDirty = false;
      this.pendingLocationIds = null;
      this.applySelectedLocations(this.savedLocationIds);
      this.restrictMode = this.savedLocationIds.length > 0 ? 'specific' : 'all';
    }
  }

  onRestrictModeChange(mode: 'all' | 'specific'){
    this.restrictMode = mode;
    if (mode === 'all'){
      this.locationsError = '';
    }
  }

  saveLocations(){
    const filtered = this.filteredSelectedLocations();
    if (this.restrictMode === 'specific' && filtered.length === 0){
      this.locationsError = 'Please select at least one location.';
      return;
    }
    this.locationsError = '';
    const ids = this.restrictMode === 'all' ? [] : [...filtered];
    const changed = !this.arraysEqual(ids, this.savedLocationIds);
    this.pendingLocationIds = changed ? ids : null;
    this.locationsDirty = changed;
    this.applySelectedLocations(changed ? ids : this.savedLocationIds);
    this.showLocationEditor = false;
    if (this.locationsDirty){
      this.toast.addToastr(new Toastr({ Type: 1, Message: 'Locations staged. Save to apply.' }));
    }
  }

  private loadAudits(){
    this.auditService.getAudits(true).subscribe({
      next: res => {
        const list = res?.Audits || [];
        this.audits = list || [];
        const placeholder = { AuditID: this.auditPlaceholderId, AuditName: 'Please Select...', Disabled: true };
        this.auditOptions = [placeholder, ...(this.audits || [])];
        if (this.actionForm.ActionVariationID === 2 && (!this.actionForm.AuditID || this.actionForm.AuditID <= 0)){
          this.actionForm.AuditID = this.auditPlaceholderId;
        }
        this.ensureValidAuditSelections();
      },
      error: () => {
        this.audits = [];
        this.auditOptions = [];
      }
    });
  }

  private loadLocations(){
    this.locationsLoading = true;
    this.locationsError = '';
    this.referenceService.getFlatLocationsForUser().subscribe({
      next: list => {
        const placeholder: AuditLocation = { LocationID: this.locationPlaceholderId, LocationName: 'Please Select...', Disabled: true } as any;
        this.locations = [placeholder, ...(list || [])];
        this.locationsLoading = false;
        this.loadWorkflowLocations();
      },
      error: () => {
        this.locations = [];
        this.locationsLoading = false;
        this.locationsError = 'Unable to load locations.';
      }
    });
  }

  private loadWorkflowLocations(){
    this.workflowService.getWorkflowLocations(this.workflowId).subscribe({
      next: ids => {
        this.savedLocationIds = ids || [];
        this.pendingLocationIds = null;
        this.locationsDirty = false;
        this.applySelectedLocations(this.savedLocationIds);
        this.restrictMode = this.savedLocationIds.length > 0 ? 'specific' : 'all';
      },
      error: error => {
        this.savedLocationIds = [];
        this.pendingLocationIds = null;
        this.locationsDirty = false;
        this.selectedLocationIds = [this.locationPlaceholderId];
        this.restrictMode = 'all';
        this.locationsError = this.workflowError(error, 'Unable to load workflow locations.');
      }
    });
  }

  private currentLocationIds(): number[]{
    if (this.locationsDirty && this.pendingLocationIds){
      return this.pendingLocationIds;
    }
    return this.savedLocationIds || [];
  }

  private applySelectedLocations(ids: number[]){
    this.selectedLocationIds = (ids && ids.length > 0) ? [...ids] : [this.locationPlaceholderId];
  }

  private filteredSelectedLocations(source: number[] = this.selectedLocationIds || []): number[]{
    return (source || []).filter(id => id !== undefined && id !== null && id > 0);
  }

  private arraysEqual(a: number[], b: number[]): boolean{
    if (a.length !== b.length){ return false; }
    const sortedA = [...a].sort((x,y) => x - y);
    const sortedB = [...b].sort((x,y) => x - y);
    return sortedA.every((val, idx) => val === sortedB[idx]);
  }

  private ensureValidAuditSelections(){
    if (!this.auditOptions || this.auditOptions.length === 0){
      return;
    }
    const hasAudit = (auditId?: number | null) => this.auditOptions.some(opt => opt.AuditID === auditId);
    this.actions = (this.actions || []).map(a => {
      const updated = { ...a };
      if (updated.ActionVariationID === 2){
        if (!updated.AuditID || updated.AuditID <= 0 || !hasAudit(updated.AuditID)){
          updated.AuditID = this.auditPlaceholderId;
        }
      } else {
        updated.AuditID = null;
      }
      return updated;
    });
  }
}
